import { userType } from "../../DTO";
import { Api } from "../api";

export const FOOD_UNIT_KEYS = [
    "plate", "bowl", "cup", "serving", "piece", "slice", "scoop",
    "gram", "tablespoon", "teaspoon",
] as const;

export type FoodUnitKey = typeof FOOD_UNIT_KEYS[number];
export type ReviewStatus = "pending" | "approved" | "rejected";
export type ResolutionStatus = "enriching" | "ready" | "failed" | "inactive";

export interface PortionOption {
    id?: number;
    unit_key?: FoodUnitKey;
    portion_unit_id?: number;
    label: string;
    allowed_amounts: number[] | null;
    is_default: boolean;
    sort_order: number;
    is_active?: boolean;
    unit?: { id: number; key: FoodUnitKey; name?: string };
}

export interface CatalogFood {
    id: number;
    name: string;
    aliases: string[];
    portion_options: PortionOption[];
    origin: "admin" | "ai";
    review_status: ReviewStatus;
    resolution_status: ResolutionStatus;
    usage_count: number;
    last_error: string | null;
    updated_at: string;
}

export interface FoodPayload {
    name: string;
    aliases: string[];
    portion_options: Array<{
        unit_key: FoodUnitKey;
        label: string;
        allowed_amounts: number[] | null;
        is_default: boolean;
        sort_order: number;
    }>;
}

export interface CatalogFilters {
    q?: string;
    origin?: string;
    review_status?: string;
    resolution_status?: string;
    per_page: number;
    page: number;
}

export interface CatalogPage {
    data: CatalogFood[];
    current_page: number;
    per_page: number;
    total: number;
}

const auth = (user: userType) => ({ Authorization: `Bearer ${user.access_token}` });
const call = (user: userType, method: "get" | "post" | "put" | "delete", path: string, body?: any, params?: any) =>
    new Api({ method, path, body, params, header: auth(user), reject422: true }).call() as Promise<any>;

export const listFoods = (user: userType, filters: CatalogFilters): Promise<CatalogPage> =>
    call(user, "get", "food-catalog", undefined, filters).then((response) => response.data);

export const getFood = (user: userType, id: number): Promise<CatalogFood> =>
    call(user, "get", `food-catalog/${id}`).then((response) => response.data);

export const createFood = (user: userType, payload: FoodPayload): Promise<CatalogFood> =>
    call(user, "post", "food-catalog", payload).then((response) => response.data);

export const updateFood = (user: userType, id: number, payload: FoodPayload): Promise<CatalogFood> =>
    call(user, "put", `food-catalog/${id}`, payload).then((response) => response.data);

export const approveFood = (user: userType, id: number) => call(user, "post", `food-catalog/${id}/approve`);
export const rejectFood = (user: userType, id: number) => call(user, "post", `food-catalog/${id}/reject`);
export const retryFood = (user: userType, id: number) => call(user, "post", `food-catalog/${id}/retry-enrichment`);
export const mergeFood = (user: userType, id: number, targetFoodId: number) =>
    call(user, "post", `food-catalog/${id}/merge`, { target_food_id: targetFoodId });
export const deactivateFood = (user: userType, id: number) => call(user, "delete", `food-catalog/${id}`);
